import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Autocomplete,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  LinkOff as LinkOffIcon,
  Link as LinkIcon,
  PersonAdd as PersonAddIcon,
  LocalShipping as BookingsIcon,
  LocalOffer as OffersIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tmsApi } from '../api/client';
import { TmsCustomerMapping } from '../types';

interface CustomerSearchResult {
  id: string;
  name: string;
  cvr?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  status: string;
  tmsCustomerId?: string;
}

export const UnmappedBookings: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);

  // Bookings state
  const [bookingPage, setBookingPage] = useState(1);
  const [bookingLinkingId, setBookingLinkingId] = useState<string | null>(null);
  const [bookingSearchInput, setBookingSearchInput] = useState('');
  const [bookingSearchTerm, setBookingSearchTerm] = useState('');
  const [bookingSelectedCustomer, setBookingSelectedCustomer] = useState<CustomerSearchResult | null>(null);
  const [bookingCreateDialog, setBookingCreateDialog] = useState<TmsCustomerMapping | null>(null);
  const [bookingCreateForm, setBookingCreateForm] = useState({ name: '', cvr: '', email: '', phone: '', address: '', city: '', postalCode: '', country: 'Danmark' });

  // Offers state
  const [offerPage, setOfferPage] = useState(1);
  const [offerLinkingId, setOfferLinkingId] = useState<string | null>(null);
  const [offerSearchInput, setOfferSearchInput] = useState('');
  const [offerSearchTerm, setOfferSearchTerm] = useState('');
  const [offerSelectedCustomer, setOfferSelectedCustomer] = useState<CustomerSearchResult | null>(null);
  const [offerCreateDialog, setOfferCreateDialog] = useState<TmsCustomerMapping | null>(null);
  const [offerCreateForm, setOfferCreateForm] = useState({ name: '', cvr: '', email: '', phone: '', address: '', city: '', postalCode: '', country: 'Danmark' });

  // Debounce booking search
  useEffect(() => {
    const timer = setTimeout(() => setBookingSearchTerm(bookingSearchInput), 300);
    return () => clearTimeout(timer);
  }, [bookingSearchInput]);

  // Debounce offer search
  useEffect(() => {
    const timer = setTimeout(() => setOfferSearchTerm(offerSearchInput), 300);
    return () => clearTimeout(timer);
  }, [offerSearchInput]);

  // Bookings query
  const { data: bookingData, isLoading: bookingsLoading } = useQuery({
    queryKey: ['unmapped-bookings', bookingPage],
    queryFn: async () => {
      const res = await tmsApi.getUnmappedBookings({ page: bookingPage, limit: 50 });
      return res.data.data;
    },
  });

  // Offers query
  const { data: offerData, isLoading: offersLoading } = useQuery({
    queryKey: ['unmapped-offers', offerPage],
    queryFn: async () => {
      const res = await tmsApi.getUnmappedOffers({ page: offerPage, limit: 50 });
      return res.data.data;
    },
  });

  // Search queries (shared — only one active at a time)
  const activeSearchTerm = activeTab === 0 ? bookingSearchTerm : offerSearchTerm;
  const { data: searchResults } = useQuery({
    queryKey: ['customer-search', activeSearchTerm],
    queryFn: async () => {
      const res = await tmsApi.searchCustomersForMapping(activeSearchTerm);
      return res.data.data as CustomerSearchResult[];
    },
    enabled: activeSearchTerm.length >= 2,
  });

  // Booking mutations
  const bookingLinkMutation = useMutation({
    mutationFn: ({ mappingId, customerId }: { mappingId: string; customerId: string }) =>
      tmsApi.linkCustomerMapping(mappingId, customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unmapped-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['tms-sync-status'] });
      setBookingLinkingId(null);
      setBookingSelectedCustomer(null);
      setBookingSearchInput('');
    },
  });

  const bookingCreateMutation = useMutation({
    mutationFn: ({ mappingId, data }: { mappingId: string; data: any }) =>
      tmsApi.createCustomerFromMapping(mappingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unmapped-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['tms-sync-status'] });
      setBookingCreateDialog(null);
    },
  });

  // Offer mutations
  const offerLinkMutation = useMutation({
    mutationFn: ({ mappingId, customerId }: { mappingId: string; customerId: string }) =>
      tmsApi.linkOfferMapping(mappingId, customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unmapped-offers'] });
      queryClient.invalidateQueries({ queryKey: ['tms-sync-status'] });
      setOfferLinkingId(null);
      setOfferSelectedCustomer(null);
      setOfferSearchInput('');
    },
  });

  const offerCreateMutation = useMutation({
    mutationFn: ({ mappingId, data }: { mappingId: string; data: any }) =>
      tmsApi.createCustomerFromOfferMapping(mappingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unmapped-offers'] });
      queryClient.invalidateQueries({ queryKey: ['tms-sync-status'] });
      setOfferCreateDialog(null);
    },
  });

  const bookingMappings: TmsCustomerMapping[] = bookingData?.mappings || [];
  const bookingPagination = bookingData?.pagination;
  const totalPendingBookings: number = bookingData?.totalPendingBookings || 0;

  const offerMappings: TmsCustomerMapping[] = offerData?.mappings || [];
  const offerPagination = offerData?.pagination;
  const totalPendingOffers: number = offerData?.totalPendingOffers || 0;

  const openBookingCreateDialog = (mapping: TmsCustomerMapping) => {
    setBookingCreateForm({
      name: mapping.tmsCustomerName || '',
      cvr: '', email: '', phone: '', address: '', city: '', postalCode: '', country: 'Danmark',
    });
    setBookingCreateDialog(mapping);
  };

  const openOfferCreateDialog = (mapping: TmsCustomerMapping) => {
    setOfferCreateForm({
      name: mapping.tmsCustomerName || '',
      cvr: '', email: '', phone: '', address: '', city: '', postalCode: '', country: 'Danmark',
    });
    setOfferCreateDialog(mapping);
  };

  const renderMappingTable = (
    mappings: TmsCustomerMapping[],
    countField: 'pendingBookingsCount' | 'pendingOffersCount',
    countLabel: string,
    linkingId: string | null,
    setLinkingId: (id: string | null) => void,
    searchInput: string,
    setSearchInput: (v: string) => void,
    selectedCustomer: CustomerSearchResult | null,
    setSelectedCustomer: (c: CustomerSearchResult | null) => void,
    linkMutation: any,
    openCreateDialog: (m: TmsCustomerMapping) => void,
    pagination: any,
    page: number,
    setPage: (p: number) => void,
    totalPending: number,
    emptyMessage: string,
  ) => {
    if (mappings.length === 0) {
      return (
        <Alert severity="success" sx={{ fontSize: '0.75rem' }}>
          {emptyMessage}
        </Alert>
      );
    }

    return (
      <>
        {totalPending > 0 && (
          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                {totalPending} ventende {countLabel} — {pagination?.total || 0} kunder uden mapping
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={pagination?.total ? Math.max(0, 100 - ((pagination.total / (pagination.total + 1)) * 100)) : 0}
              sx={{ height: 6, borderRadius: 1 }}
            />
          </Box>
        )}

        <Card sx={{ borderRadius: 1 }}>
          <CardContent sx={{ p: '8px !important' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Kunde</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, textAlign: 'center' }}>Ventende {countLabel}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Forslag</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Handling</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings.map((mapping) => (
                    <TableRow key={mapping.id} hover>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          {mapping.tmsCustomerName || '-'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.25 }}>
                          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                            Nr: {mapping.tmsCustomerId}
                          </Typography>
                          {mapping.tmsVatNumber && (
                            <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                              CVR: {mapping.tmsVatNumber}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={countField === 'pendingBookingsCount' ? mapping.pendingBookingsCount : mapping.pendingOffersCount}
                          color="warning"
                          size="small"
                          sx={{ fontSize: '0.65rem', height: 20, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        {mapping.suggestedCustomer ? (
                          <Tooltip title={mapping.matchReason || 'Foreslået match'}>
                            <Chip
                              label={mapping.suggestedCustomer.name}
                              color={mapping.matchConfidence === 'HIGH' ? 'success' : mapping.matchConfidence === 'MEDIUM' ? 'info' : 'default'}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.6rem', height: 22, maxWidth: 180 }}
                              onDelete={() => {
                                if (mapping.suggestedCustomerId) {
                                  linkMutation.mutate({ mappingId: mapping.id, customerId: mapping.suggestedCustomerId });
                                }
                              }}
                              deleteIcon={<CheckIcon sx={{ fontSize: '0.7rem !important' }} />}
                            />
                          </Tooltip>
                        ) : (
                          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {linkingId === mapping.id ? (
                          <Box display="flex" gap={0.5} alignItems="center">
                            <Autocomplete
                              size="small"
                              options={searchResults || []}
                              getOptionLabel={(c) => `${c.name}${c.cvr ? ` (${c.cvr})` : ''}`}
                              filterOptions={(x) => x}
                              onChange={(_, value) => setSelectedCustomer(value)}
                              onInputChange={(_, value) => setSearchInput(value)}
                              loading={activeSearchTerm.length >= 2 && !searchResults}
                              noOptionsText={activeSearchTerm.length < 2 ? 'Skriv mindst 2 tegn...' : 'Ingen resultater'}
                              renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                  <Box>
                                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>{option.name}</Typography>
                                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                                      {[option.cvr, option.email, option.city].filter(Boolean).join(' · ')}
                                    </Typography>
                                  </Box>
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Sog kunde..."
                                  sx={{ minWidth: 250, '& input': { fontSize: '0.7rem' } }}
                                />
                              )}
                            />
                            <Button
                              variant="contained"
                              size="small"
                              disabled={!selectedCustomer || linkMutation.isPending}
                              onClick={() => selectedCustomer && linkMutation.mutate({ mappingId: mapping.id, customerId: selectedCustomer.id })}
                              startIcon={<LinkIcon sx={{ fontSize: '0.7rem' }} />}
                              sx={{ fontSize: '0.65rem', minWidth: 70 }}
                            >
                              {linkMutation.isPending ? '...' : 'Kobl'}
                            </Button>
                            <Button
                              size="small"
                              onClick={() => { setLinkingId(null); setSelectedCustomer(null); setSearchInput(''); }}
                              sx={{ fontSize: '0.65rem' }}
                            >
                              Annuller
                            </Button>
                          </Box>
                        ) : (
                          <Box display="flex" gap={0.5}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setLinkingId(mapping.id)}
                              startIcon={<LinkIcon sx={{ fontSize: '0.7rem' }} />}
                              sx={{ fontSize: '0.65rem', borderRadius: 1 }}
                            >
                              Kobl
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="secondary"
                              onClick={() => openCreateDialog(mapping)}
                              startIcon={<PersonAddIcon sx={{ fontSize: '0.7rem' }} />}
                              sx={{ fontSize: '0.65rem', borderRadius: 1 }}
                            >
                              Opret ny
                            </Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {pagination && pagination.totalPages > 1 && (
              <Box display="flex" justifyContent="center" gap={1} mt={1}>
                <Button
                  size="small"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  sx={{ fontSize: '0.65rem' }}
                >
                  Forrige
                </Button>
                <Typography sx={{ fontSize: '0.7rem', lineHeight: '30px' }}>
                  Side {page} af {pagination.totalPages}
                </Typography>
                <Button
                  size="small"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage(page + 1)}
                  sx={{ fontSize: '0.65rem' }}
                >
                  Naeste
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </>
    );
  };

  const renderCreateDialog = (
    createDialogMapping: TmsCustomerMapping | null,
    setCreateDialogMapping: (m: TmsCustomerMapping | null) => void,
    createForm: any,
    setCreateForm: (f: any) => void,
    createMutation: any,
    label: string,
  ) => (
    <Dialog open={!!createDialogMapping} onClose={() => setCreateDialogMapping(null)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
        Opret ny kunde ({label})
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 2 }}>
          Kunden oprettes og kobles automatisk til TMS-kunden "{createDialogMapping?.tmsCustomerName}".
        </Typography>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Navn"
              fullWidth
              size="small"
              required
              value={createForm.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreateForm({ ...createForm, name: e.target.value })}
              InputProps={{ sx: { fontSize: '0.75rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="CVR"
              fullWidth
              size="small"
              value={createForm.cvr}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreateForm({ ...createForm, cvr: e.target.value })}
              InputProps={{ sx: { fontSize: '0.75rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="Email"
              fullWidth
              size="small"
              value={createForm.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreateForm({ ...createForm, email: e.target.value })}
              InputProps={{ sx: { fontSize: '0.75rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="Telefon"
              fullWidth
              size="small"
              value={createForm.phone}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreateForm({ ...createForm, phone: e.target.value })}
              InputProps={{ sx: { fontSize: '0.75rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="Adresse"
              fullWidth
              size="small"
              value={createForm.address}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreateForm({ ...createForm, address: e.target.value })}
              InputProps={{ sx: { fontSize: '0.75rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <TextField
              label="By"
              fullWidth
              size="small"
              value={createForm.city}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreateForm({ ...createForm, city: e.target.value })}
              InputProps={{ sx: { fontSize: '0.75rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <TextField
              label="Postnr"
              fullWidth
              size="small"
              value={createForm.postalCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreateForm({ ...createForm, postalCode: e.target.value })}
              InputProps={{ sx: { fontSize: '0.75rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <TextField
              label="Land"
              fullWidth
              size="small"
              value={createForm.country}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreateForm({ ...createForm, country: e.target.value })}
              InputProps={{ sx: { fontSize: '0.75rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateDialogMapping(null)} sx={{ fontSize: '0.7rem' }}>
          Annuller
        </Button>
        <Button
          variant="contained"
          disabled={!createForm.name.trim() || createMutation.isPending}
          onClick={() => createDialogMapping && createMutation.mutate({
            mappingId: createDialogMapping.id,
            data: createForm,
          })}
          sx={{ fontSize: '0.7rem' }}
        >
          {createMutation.isPending ? 'Opretter...' : 'Opret og kobl'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const isLoading = activeTab === 0 ? bookingsLoading : offersLoading;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <LinkOffIcon sx={{ fontSize: '1.2rem', color: 'warning.main' }} />
        <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1rem' }}>
          Kunder uden kobling
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={2} sx={{ fontSize: '0.75rem' }}>
        TMS-kunder der ikke er koblet til en CRM-kunde. Kobl dem til en eksisterende kunde eller opret en ny.
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontSize: '0.75rem', textTransform: 'none' } }}
      >
        <Tab
          icon={<BookingsIcon sx={{ fontSize: '0.9rem' }} />}
          iconPosition="start"
          label={`Bookings${bookingPagination?.total ? ` (${bookingPagination.total})` : ''}`}
        />
        <Tab
          icon={<OffersIcon sx={{ fontSize: '0.9rem' }} />}
          iconPosition="start"
          label={`Tilbud${offerPagination?.total ? ` (${offerPagination.total})` : ''}`}
        />
      </Tabs>

      {activeTab === 0 && renderMappingTable(
        bookingMappings,
        'pendingBookingsCount',
        'bookings',
        bookingLinkingId,
        setBookingLinkingId,
        bookingSearchInput,
        setBookingSearchInput,
        bookingSelectedCustomer,
        setBookingSelectedCustomer,
        bookingLinkMutation,
        openBookingCreateDialog,
        bookingPagination,
        bookingPage,
        setBookingPage,
        totalPendingBookings,
        'Alle kunder med bookings er koblet! Kor en synkronisering fra Indstillinger for at opdatere.',
      )}

      {activeTab === 1 && renderMappingTable(
        offerMappings,
        'pendingOffersCount',
        'tilbud',
        offerLinkingId,
        setOfferLinkingId,
        offerSearchInput,
        setOfferSearchInput,
        offerSelectedCustomer,
        setOfferSelectedCustomer,
        offerLinkMutation,
        openOfferCreateDialog,
        offerPagination,
        offerPage,
        setOfferPage,
        totalPendingOffers,
        'Alle kunder med tilbud er koblet! Kor en synkronisering fra Indstillinger for at opdatere.',
      )}

      {renderCreateDialog(
        bookingCreateDialog,
        setBookingCreateDialog,
        bookingCreateForm,
        setBookingCreateForm,
        bookingCreateMutation,
        'Bookings',
      )}

      {renderCreateDialog(
        offerCreateDialog,
        setOfferCreateDialog,
        offerCreateForm,
        setOfferCreateForm,
        offerCreateMutation,
        'Tilbud',
      )}
    </Box>
  );
};

export default UnmappedBookings;
